import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { CSSProperties } from "react";
import QRCode from "qrcode";
import {
  ApiError,
  PouchPayClient,
  type BankNetwork,
  type CreateSessionRequest,
  type CryptoChannelsResponse,
  type KycRequirement,
  type QuoteResponse,
  type RateResponse,
  type SessionResponse,
} from "@pouchpay/sdk";

// ─── Types ────────────────────────────────────────────────────────────────────
type Theme = Record<string, CSSProperties>;

type KycField = {
  documentType: string;
  label: string;
  required: boolean;
};

export type PouchPayModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  sessionId?: string;
  createSession?: CreateSessionRequest;
  apiBaseUrl?: string;
  apiKey?: string;
  variant?: "modal" | "inline";
  pollIntervalMs?: number;
  onSessionUpdate?: (session: SessionResponse) => void;
  onError?: (message: string) => void;
  theme?: "light" | "dark";
};

// ─── Constants ────────────────────────────────────────────────────────────────
const TERMINAL = ["COMPLETED", "FAILED", "KYC_FAILED"];
const POLLING = [
  "PENDING_KYC",
  "PENDING_PAYMENT",
  "PAYMENT_PENDING",
  "PAYMENT_RECEIVED",
  "PAYOUT_PENDING",
  "PAYOUT_PROCESSING",
  "PROCESSING_PAYOUT",
  "EXPIRED"
];
const OTP_LEN = 6;

// Common country codes
const COUNTRY_CODES = [
  { code: "+234", country: "Nigeria", flag: "🇳🇬" },
  { code: "+1", country: "United States", flag: "🇺🇸" },
  { code: "+44", country: "United Kingdom", flag: "🇬🇧" },
  { code: "+91", country: "India", flag: "🇮🇳" },
  { code: "+86", country: "China", flag: "🇨🇳" },
  { code: "+81", country: "Japan", flag: "🇯🇵" },
  { code: "+49", country: "Germany", flag: "🇩🇪" },
  { code: "+33", country: "France", flag: "🇫🇷" },
  { code: "+39", country: "Italy", flag: "🇮🇹" },
  { code: "+27", country: "South Africa", flag: "🇿🇦" },
  { code: "+254", country: "Kenya", flag: "🇰🇪" },
  { code: "+233", country: "Ghana", flag: "🇬🇭" },
  { code: "+256", country: "Uganda", flag: "🇺🇬" },
];

const STEPS_ONRAMP = ["COLLECT_EMAIL", "VERIFY_OTP", "KYC", "AWAIT_PAYMENT"];
const STEPS_OFFRAMP = [
  "COLLECT_EMAIL",
  "VERIFY_OTP",
  "KYC",
  "BANK_ACCOUNT",
  "AWAIT_PAYMENT",
];

function getStepIndex(session: SessionResponse, localKycStep: string | null): number {
  const steps = session.type === "OFFRAMP" ? STEPS_OFFRAMP : STEPS_ONRAMP;

  // Determine current step from state flags
  let currentStep: string;
  if (!session.emailVerified) {
    currentStep = session.status === 'PENDING_OTP' ? 'VERIFY_OTP' : 'COLLECT_EMAIL';
  } else if (session.type === 'OFFRAMP' && !session.bankAccountProvided) {
    currentStep = 'BANK_ACCOUNT';
  } else if (!session.kycVerified) {
    currentStep = localKycStep || 'KYC_ID';
  } else {
    currentStep = 'AWAIT_PAYMENT';
  }

  const i = steps.indexOf(currentStep);
  return i >= 0 ? i : 0;
}

// ─── Global CSS ───────────────────────────────────────────────────────────────
const GCSS = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
body{font-family:'DM Sans',sans-serif}
@keyframes ppFadeIn{from{opacity:0;transform:translateY(12px) scale(.98)}to{opacity:1;transform:none}}
@keyframes ppBackdrop{from{opacity:0}to{opacity:1}}
@keyframes ppSpin{to{transform:rotate(360deg)}}
@keyframes ppShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
@keyframes ppPulse{0%,100%{opacity:.5}50%{opacity:1}}
@keyframes ppSlideUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.pp-input{width:100%;background:#f7f8fa;border:1.5px solid #eaecf0;border-radius:12px;color:#111;padding:13px 16px;font-size:15px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .15s,box-shadow .15s}
.pp-input:focus{border-color:#0066ff;box-shadow:0 0 0 3px rgba(0,102,255,.1);background:#fff}
.pp-input::placeholder{color:#adb5bd}
.pp-input-dark{background:#1a1d27;border-color:#2a2d3a;color:#f0f1f5}
.pp-input-dark:focus{border-color:#3d7eff;box-shadow:0 0 0 3px rgba(61,126,255,.1);background:#1e2130}
.pp-input-dark::placeholder{color:#4a4f6a}
.pp-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:14px;border-radius:12px;border:none;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:all .15s;letter-spacing:-.01em}
.pp-btn:disabled{opacity:.45;cursor:not-allowed}
.pp-btn-primary{background:#0066ff;color:#fff}
.pp-btn-primary:not(:disabled):hover{background:#0052d6;transform:translateY(-1px);box-shadow:0 4px 16px rgba(0,102,255,.3)}
.pp-btn-primary:not(:disabled):active{transform:none;box-shadow:none}
.pp-btn-ghost{background:transparent;color:#6b7280;border:1.5px solid #eaecf0}
.pp-btn-ghost:not(:disabled):hover{background:#f7f8fa;color:#111}
.pp-btn-dark-primary{background:#3d7eff;color:#fff}
.pp-btn-dark-primary:not(:disabled):hover{background:#5c90ff;transform:translateY(-1px);box-shadow:0 4px 16px rgba(61,126,255,.3)}
.pp-btn-dark-ghost{background:transparent;color:#8892a4;border:1.5px solid #2a2d3a}
.pp-btn-dark-ghost:not(:disabled):hover{background:#1e2130;color:#f0f1f5}
.pp-otp-inp{width:44px;height:52px;text-align:center;font-family:'DM Mono',monospace;font-size:22px;font-weight:500;border-radius:10px;border:1.5px solid #eaecf0;background:#f7f8fa;color:#111;outline:none;transition:all .15s}
.pp-otp-inp:focus{border-color:#0066ff;box-shadow:0 0 0 3px rgba(0,102,255,.1);background:#fff}
.pp-otp-inp.filled{border-color:#0066ff;color:#0066ff;background:#f0f5ff}
.pp-otp-inp-dark{background:#1a1d27;border-color:#2a2d3a;color:#f0f1f5}
.pp-otp-inp-dark:focus{border-color:#3d7eff;box-shadow:0 0 0 3px rgba(61,126,255,.1)}
.pp-otp-inp-dark.filled{border-color:#3d7eff;color:#3d7eff;background:#1a2240}
.pp-link{background:none;border:none;font-family:'DM Sans',sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s;text-decoration:none}
.pp-slide{animation:ppSlideUp .22s ease both}
`;

// ─── PouchPayModal ────────────────────────────────────────────────────────────
export function PouchPayModal({
  isOpen: isOpenProp,
  onClose,
  sessionId: providedSessionId,
  createSession,
  apiBaseUrl,
  apiKey,
  variant = "modal",
  pollIntervalMs = 4000,
  onSessionUpdate,
  onError,
  theme = "light",
}: PouchPayModalProps) {
  // For inline variant, always consider it "open"
  const isOpen = variant === "inline" ? true : (isOpenProp ?? false);
  const isDark = theme === "dark";
  const client = useMemo(
    () => new PouchPayClient({ baseUrl: apiBaseUrl, apiKey }),
    [apiBaseUrl, apiKey],
  );

  const [session, setSession] = useState<SessionResponse | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(
    providedSessionId ?? null,
  );
  const sessionRef = useRef<SessionResponse | null>(null);
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [kycReq, setKycReq] = useState<KycRequirement | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LEN).fill(""));
  const [otpErr, setOtpErr] = useState<string | null>(null);
  const [kycDocs, setKycDocs] = useState<Record<string, string>>({});
  const [kycErr, setKycErr] = useState<string | null>(null);
  const [localKycStep, setLocalKycStep] = useState<"KYC_ID" | "KYC" | null>(null); // Track KYC step locally
  const [bankVals, setBankVals] = useState({
    accountNumber: "",
    networkId: "",
  });
  const [bankErr, setBankErr] = useState<string | null>(null);
  const [bankResolved, setBankResolved] = useState<{
    accountName: string;
    bankName: string;
  } | null>(null);
  const [bankNets, setBankNets] = useState<BankNetwork[]>([]);
  const [netsLoading, setNetsLoading] = useState(false);
  const [rateLock, setRateLock] = useState(0);

  // Preview state
  const [previewConfirmed, setPreviewConfirmed] = useState(false);
  const [estimatedRate, setEstimatedRate] = useState<RateResponse | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [channels, setChannels] = useState<CryptoChannelsResponse | null>(null);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [cryptoPrice, setCryptoPrice] = useState<number | null>(null);
  const [cryptoPriceLoading, setCryptoPriceLoading] = useState(false);

  const isTerminal = session ? TERMINAL.includes(session.status) : false;

  const handleErr = useCallback(
    (msg: string) => {
      setError(msg);
      onError?.(msg);
    },
    [onError],
  );

  const updateSession = useCallback(
    (data: SessionResponse) => {
      setSession(data);
      setSessionId(data.id);
      sessionRef.current = data;
      onSessionUpdate?.(data);
      if (data.kycRequirement) setKycReq(data.kycRequirement);
    },
    [onSessionUpdate],
  );

  const doRefreshQuote = useCallback(
    async (s?: SessionResponse) => {
      const target = s ?? sessionRef.current;
      if (!target) return;
      try {
        setQuoteLoading(true);
        const q = await client.getQuote(target.id);
        setQuote(q);
      } catch {
      } finally {
        setQuoteLoading(false);
      }
    },
    [client],
  );

  const doRefreshSession = useCallback(
    async (opts?: { quote?: boolean; kyc?: boolean }) => {
      const id = sessionRef.current?.id;
      if (!id) return;
      try {
        const data = await client.getSession(id);
        updateSession(data);
        if (opts?.quote !== false) await doRefreshQuote(data);
        if (opts?.kyc !== false && !data.kycVerified) {
          if (!data.kycRequirement) {
            const r = await client.getKycRequirements(id);
            setKycReq(r);
          }
        }
      } catch (e) {
        handleErr(e instanceof ApiError ? e.message : "Failed to refresh");
      }
    },
    [client, doRefreshQuote, handleErr, updateSession],
  );

  // Sync external sessionId prop
  useEffect(() => {
    if (providedSessionId) setSessionId(providedSessionId);
  }, [providedSessionId]);

  // Init on open - use a ref to track if we've initialized
  const initializedRef = useRef(false);

  useEffect(() => {
    if (variant === "modal" && !isOpen) {
      // Reset initialization flag when modal closes
      initializedRef.current = false;
      return;
    }

    // Prevent re-initialization if already initialized AND we have session data
    if (initializedRef.current && session) {
      console.log("Already initialized with session, skipping re-initialization");
      return;
    }

    console.log("Initializing session...");
    initializedRef.current = true;

    setSession(null);
    sessionRef.current = null;
    setQuote(null);
    setKycReq(null);
    setError(null);
    setEmail("");
    setOtpDigits(Array(OTP_LEN).fill(""));
    setKycDocs({});
    setBankVals({ accountNumber: "", networkId: "" });
    setBankResolved(null);
    setRateLock(0);
    setPreviewConfirmed(false);
    setEstimatedRate(null);
    setLoading(true);

    let cancelled = false;
    async function init() {
      try {
        // For new sessions with createSession, fetch rate first for preview
        if (createSession && !providedSessionId) {
          console.log("Fetching rate for preview...");
          setRateLoading(true);
          try {
            const isStablecoin = ['USDT', 'USDC', 'DAI', 'USDD'].includes(createSession.cryptoCurrency);

            let fromCurrency: string;
            let toCurrency: string;
            let rateAmount: number;
            let rateType: 'BUY' | 'SELL';

            if (createSession.type === 'ONRAMP') {
              // For onramp: we're buying crypto with fiat
              // Convert the fiat amount to USD (rate endpoint only supports toCurrency=USD)
              fromCurrency = createSession.currency;
              toCurrency = 'USD';
              rateAmount = createSession.amount || createSession.amountFiat || 100;
              rateType = 'BUY';
            } else {
              // For offramp: we're selling crypto for fiat
              // Rate endpoint only supports toCurrency=USD, so we get the fiat→USD rate
              // Then we'll reverse it in the calculation (multiply instead of divide)
              fromCurrency = createSession.currency;
              toCurrency = 'USD';
              rateAmount = 100; // Get rate for 100 fiat units
              rateType = 'SELL';
            }

            const rateData = await client.getRate({
              amount: rateAmount,
              fromCurrency,
              toCurrency,
              type: rateType,
              countryCode: createSession.countryCode,
            });

            if (!cancelled) {
              setEstimatedRate(rateData);
              console.log("Rate fetched for preview:", rateData);
            }
          } catch (e) {
            console.error("Rate fetch error:", e);
            // Continue anyway, just won't show rate
          } finally {
            if (!cancelled) setRateLoading(false);
          }

          // Fetch crypto price for non-stablecoins
          const isStablecoin = ['USDT', 'USDC', 'DAI', 'USDD', 'BUSD'].includes(createSession.cryptoCurrency);
          if (!isStablecoin) {
            setCryptoPriceLoading(true);
            try {
              const priceData = await client.getCryptoPrice(createSession.cryptoCurrency);
              if (!cancelled) {
                setCryptoPrice(priceData.priceUsd);
                console.log("Crypto price fetched for preview:", priceData);
              }
            } catch (e) {
              console.error("Crypto price fetch error:", e);
              // Continue anyway, will use 1:1 USD assumption
            } finally {
              if (!cancelled) setCryptoPriceLoading(false);
            }
          } else {
            // Stablecoins are 1 USD
            setCryptoPrice(1);
          }

          // Fetch crypto channels for validation
          setChannelsLoading(true);
          try {
            const channelsData = await client.getCryptoChannels();
            if (!cancelled) {
              setChannels(channelsData);
              console.log("Channels fetched for preview:", channelsData);
            }
          } catch (e) {
            console.error("Channels fetch error:", e);
            // Continue anyway, validation will be skipped
          } finally {
            if (!cancelled) setChannelsLoading(false);
          }

          // Stop here - wait for user to confirm preview
          if (!cancelled) setLoading(false);
          return;
        }

        // For existing sessions or after preview confirmed, proceed with normal flow
        let data: SessionResponse;
        if (providedSessionId) {
          console.log("Fetching existing session:", providedSessionId);
          data = await client.getSession(providedSessionId);
          console.log("Session fetched:", data);
        } else if (createSession) {
          console.log("Creating new session with payload:", createSession);
          data = await client.createSession(createSession);
          console.log("Session created:", data);
        } else {
          handleErr("Provide either sessionId or createSession.");
          return;
        }
        if (cancelled) return;

        console.log("Updating session state...");
        updateSession(data);
        console.log("Session state updated");

        console.log("Fetching quote...");
        await doRefreshQuote(data);
        console.log("Quote fetched");

        if (!data.kycVerified && !data.kycRequirement) {
          const r = await client.getKycRequirements(data.id);
          if (!cancelled) setKycReq(r);
        }
      } catch (e) {
        console.error("Init error:", e);
        if (!cancelled)
          handleErr(e instanceof ApiError ? e.message : "Failed to start");
      } finally {
        console.log("Setting loading to false, cancelled:", cancelled);
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, variant]);
  // Note: createSession, providedSessionId, client, sessionId intentionally omitted to prevent re-init
  // Only re-initialize when modal opens/closes

  // Create session after preview confirmation
  useEffect(() => {
    if (!previewConfirmed || session || !createSession || providedSessionId) return;

    const sessionPayload = createSession; // Capture for closure
    let cancelled = false;
    async function createSessionAfterPreview() {
      try {
        setLoading(true);
        console.log("Creating session after preview confirmation:", sessionPayload);
        const data = await client.createSession(sessionPayload);
        if (cancelled) return;

        console.log("Session created:", data);
        updateSession(data);

        console.log("Fetching quote...");
        await doRefreshQuote(data);

        if (!data.kycVerified && !data.kycRequirement) {
          const r = await client.getKycRequirements(data.id);
          if (!cancelled) setKycReq(r);
        }
      } catch (e) {
        console.error("Session creation error:", e);
        if (!cancelled)
          handleErr(e instanceof ApiError ? e.message : "Failed to create session");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    createSessionAfterPreview();
    return () => {
      cancelled = true;
    };
  }, [previewConfirmed, session, createSession, providedSessionId, client, updateSession, doRefreshQuote, handleErr]);

  // OTP reset
  useEffect(() => {
    if (session && !session.emailVerified && session.status === 'PENDING_OTP')
      setOtpDigits(Array(OTP_LEN).fill(""));
  }, [session?.emailVerified, session?.status]);

  // Email prefill into KYC docs
  useEffect(() => {
    if (!email || !kycReq || kycReq.type !== "field_based") return;
    if (!kycReq.fields?.some((f) => f.documentType === "EMAIL")) return;
    if (kycDocs.EMAIL === email) return;
    setKycDocs((prev) => ({ ...prev, EMAIL: email }));
  }, [email, kycReq]);

  // Reset local KYC step when session moves away from KYC
  useEffect(() => {
    if (!session) return;
    if (session.kycVerified) {
      setLocalKycStep(null);
    }
  }, [session?.kycVerified]);

  // Polling
  useEffect(() => {
    if (!sessionId || isTerminal || !session) return;
    if (!POLLING.includes(session.status)) return;

    // Use a ref to get latest session status without recreating interval
    const doRefresh = () => {
      const currentSession = sessionRef.current;
      if (!currentSession) return;

      doRefreshSession({
        quote: false,
        kyc: currentSession.status === "PENDING_KYC",
      });
    };

    const iv = setInterval(doRefresh, pollIntervalMs);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTerminal,
    pollIntervalMs,
    sessionId,
    session?.status,
    // Note: doRefreshSession is intentionally not a dependency to prevent interval restart
  ]);

  // Rate lock countdown
  useEffect(() => {
    if (rateLock <= 0) return;
    const t = setInterval(() => setRateLock((p) => (p <= 1 ? 0 : p - 1)), 1000);
    return () => clearInterval(t);
  }, [rateLock]);

  // Load bank networks
  useEffect(() => {
    if (!sessionId || !session) return;
    // Load bank networks when showing bank account form (offramp, email verified, bank not provided)
    if (session.type !== 'OFFRAMP' || !session.emailVerified || session.bankAccountProvided) return;

    let cancelled = false;
    setNetsLoading(true);
    client
      .getBankNetworks(sessionId)
      .then((nets) => {
        if (!cancelled) {
          nets.sort((a, b) => a.name.localeCompare(b.name));
          setBankNets(nets);
          if (nets.length === 1)
            setBankVals((p) => ({ ...p, networkId: nets[0].id }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setNetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [session?.emailVerified, session?.bankAccountProvided, session?.type, sessionId]);

  // Auto-confirm offramp after KYC to generate crypto deposit address
  useEffect(() => {
    if (!session || !sessionId) return;
    if (session.type !== 'OFFRAMP') return;
    if (!session.kycVerified) return;
    if (session.cryptoInstruction) return; // Already has crypto address
    if (actionLoading) return;

    let cancelled = false;
    const confirmOfframp = async () => {
      try {
        await client.confirmOfframp(sessionId);
        if (!cancelled) await doRefreshSession();
      } catch (e) {
        if (!cancelled) {
          console.error('Auto-confirm offramp failed:', e);
          handleErr(e instanceof ApiError ? e.message : "Failed to initiate payout");
        }
      }
    };
    confirmOfframp();
    return () => {
      cancelled = true;
    };
  }, [session?.type, session?.kycVerified, session?.cryptoInstruction, sessionId, actionLoading]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  async function doEmail() {
    if (!sessionRef.current) return;
    const norm = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(norm)) {
      setEmailErr("Enter a valid email address.");
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      await client.identify(sessionRef.current.id, norm);
      await doRefreshSession();
    } catch (e) {
      handleErr(e instanceof ApiError ? e.message : "Failed to submit email");
    } finally {
      setActionLoading(false);
    }
  }

  async function doOtp() {
    if (!sessionRef.current) return;
    const code = otpDigits.join("");
    if (code.length < OTP_LEN) {
      setOtpErr("Enter the full 6-digit code.");
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      await client.verifyOtp(sessionRef.current.id, code);
      await doRefreshSession();
    } catch (e) {
      setOtpErr(e instanceof ApiError ? e.message : "Invalid code.");
    } finally {
      setActionLoading(false);
    }
  }

  async function doResendOtp() {
    if (!sessionRef.current) return;
    setActionLoading(true);
    try {
      await client.resendOtp(sessionRef.current.id);
    } catch {
    } finally {
      setActionLoading(false);
    }
  }

  async function doKyc() {
    if (!sessionRef.current) return;
    const session = sessionRef.current;

    // Determine current KYC step (use local state if set, otherwise default to KYC_ID)
    const currentStep = localKycStep || 'KYC_ID';

    if (kycReq?.type === "field_based") {
      // Determine which fields to validate based on current step
      let fieldsToValidate = kycReq.fields ?? [];

      if (currentStep === "KYC_ID") {
        // Step 1: Only validate ID fields
        const idFieldTypes = ['NIN', 'BVN', 'ID_TYPE', 'ID_NUMBER'];
        fieldsToValidate = fieldsToValidate.filter(f => idFieldTypes.includes(f.documentType));

        const missing = fieldsToValidate
          .filter((f) => f.required)
          .find((f) => !kycDocs[f.documentType]);
        if (missing) {
          setKycErr(`Please enter ${missing.label}.`);
          return;
        }

        // Step 1 complete: transition to Step 2 locally (don't submit yet)
        setLocalKycStep("KYC");
        setKycErr(null);
        return;
      } else if (currentStep === "KYC") {
        // Step 2: Validate profile fields (non-ID fields)
        const idFieldTypes = ['NIN', 'BVN', 'ID_TYPE', 'ID_NUMBER'];
        const nameFieldTypes = ['FULL_NAME', 'FIRST_NAME', 'firstName', 'LAST_NAME', 'lastName', 'NAME', 'name'];

        fieldsToValidate = fieldsToValidate.filter(f => !idFieldTypes.includes(f.documentType));

        // For offramps: also skip name field validation if bank account name is available
        const metadata = session.metadata as any;
        const hasBankAccountName = session.type === 'OFFRAMP' && metadata?.bankAccountName;
        if (hasBankAccountName) {
          fieldsToValidate = fieldsToValidate.filter(f => !nameFieldTypes.includes(f.documentType));
        }

        const missing = fieldsToValidate
          .filter((f) => f.required)
          .find((f) => !kycDocs[f.documentType]);
        if (missing) {
          setKycErr(`Please enter ${missing.label}.`);
          return;
        }
      }
    }

    // Submit all KYC fields at once (both ID and profile fields)
    setActionLoading(true);
    setError(null);
    try {
      await client.submitKyc(sessionRef.current.id, kycDocs);
      await doRefreshSession();
      // Reset local KYC step after successful submission
      setLocalKycStep(null);
    } catch (e) {
      handleErr(e instanceof ApiError ? e.message : "KYC submission failed");
    } finally {
      setActionLoading(false);
    }
  }

  async function doVerifyBank() {
    if (!sessionRef.current) return;
    if (!bankVals.accountNumber || !bankVals.networkId) {
      setBankErr("Select a bank and enter an account number.");
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      const res = await client.verifyBankAccount(
        sessionRef.current.id,
        bankVals.accountNumber,
        bankVals.networkId,
      );
      setBankResolved({ accountName: res.accountName, bankName: res.bankName });
      if (session?.type === "OFFRAMP") setRateLock(600);
      await doRefreshSession();
    } catch (e) {
      handleErr(e instanceof ApiError ? e.message : "Could not verify account");
    } finally {
      setActionLoading(false);
    }
  }

  async function doProceedToKyc() {
    if (!sessionId) return;

    try {
      setActionLoading(true);
      // Call confirmOfframp to update status to PENDING_KYC (or initiate offramp if KYC already verified)
      await client.confirmOfframp(sessionId);
      // Clear bank resolved and refresh session to see updated status
      setBankResolved(null);
      await doRefreshSession();
    } catch (e) {
      console.error('Failed to proceed to KYC:', e);
      handleErr(e instanceof ApiError ? e.message : "Failed to proceed");
    } finally {
      setActionLoading(false);
    }
  }

  function doChangeAccount() {
    // Reset bank account form to allow editing
    setBankResolved(null);
    setBankVals({ accountNumber: "", networkId: "" });
  }

  function handleClose() {
    if (!actionLoading) onClose?.();
  }

  if (variant === "modal" && !isOpen) return null;

  const stepCount = (session?.type === "OFFRAMP" ? STEPS_OFFRAMP : STEPS_ONRAMP)
    .length;
  const stepIndex = session ? getStepIndex(session, localKycStep) : 0;
  const c = isDark ? dark : light;
  const isTerminalStatus =
    session &&
    TERMINAL.includes(session.status);

  console.log("Main render - State:", {
    loading,
    hasSession: !!session,
    sessionStatus: session?.status,
    isTerminalStatus
  });

  const card = (
    <div
      style={
        variant === "inline"
          ? {
              ...c.modal,
              maxWidth: "100%",
              borderRadius: 16,
              animation: "none",
              boxShadow: "none",
            }
          : c.modal
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div style={c.header}>
        <div style={c.headerLeft}>
          <div style={c.logo}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="10"
                fill={isDark ? "#3d7eff" : "#0066ff"}
                opacity=".15"
              />
              <path
                d="M8 12h8M12 8v8"
                stroke={isDark ? "#3d7eff" : "#0066ff"}
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span style={c.logoText}>PouchPay</span>
          </div>
          {!loading && session && !isTerminalStatus && (
            <DotProgress
              total={stepCount}
              current={stepIndex}
              isDark={isDark}
            />
          )}
        </div>
        {variant === "modal" && (
          <button
            style={c.closeBtn}
            onClick={handleClose}
            disabled={actionLoading}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 3l10 10M13 3L3 13"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div style={c.body}>
        {loading ? (
          <LoadingScreen isDark={isDark} c={c} />
        ) : !previewConfirmed && createSession ? (
          <PreviewStep
            createSession={createSession}
            estimatedRate={estimatedRate}
            rateLoading={rateLoading}
            channels={channels}
            channelsLoading={channelsLoading}
            cryptoPrice={cryptoPrice}
            cryptoPriceLoading={cryptoPriceLoading}
            onContinue={() => setPreviewConfirmed(true)}
            isDark={isDark}
            c={c}
          />
        ) : !session ? (
          <ErrorScreen message={error ?? "Could not start session."} c={c} />
        ) : session.status === "COMPLETED" ? (
          <SuccessScreen
            session={session}
            quote={quote}
            isDark={isDark}
            c={c}
            onClose={variant === "modal" ? handleClose : undefined}
          />
        ) : session.status === "PAYOUT_PENDING" ? (
          <PayoutPendingScreen session={session} quote={quote} isDark={isDark} c={c} />
        ) : session.status === "PAYOUT_PROCESSING" || session.status === "PROCESSING_PAYOUT" ? (
          <PayoutProcessingScreen session={session} quote={quote} isDark={isDark} c={c} />
        ) : session.status === "EXPIRED" ? (
          <ExpiredScreen session={session} c={c} isDark={isDark} />
        ) : ["FAILED", "KYC_FAILED"].includes(session.status) ? (
          <FailScreen session={session} c={c} />
        ) : (
          <StepView
            session={session}
            quote={quote}
            kycReq={kycReq}
            localKycStep={localKycStep}
            createSession={createSession}
            previewConfirmed={previewConfirmed}
            setPreviewConfirmed={setPreviewConfirmed}
            estimatedRate={estimatedRate}
            rateLoading={rateLoading}
            channels={channels}
            channelsLoading={channelsLoading}
            cryptoPrice={cryptoPrice}
            cryptoPriceLoading={cryptoPriceLoading}
            email={email}
            setEmail={(v) => {
              setEmail(v);
              setEmailErr(null);
            }}
            emailErr={emailErr}
            doEmail={doEmail}
            otpDigits={otpDigits}
            setOtpDigits={(d) => {
              setOtpDigits(d);
              setOtpErr(null);
            }}
            otpErr={otpErr}
            doOtp={doOtp}
            doResendOtp={doResendOtp}
            kycDocs={kycDocs}
            setKycDocs={(k, v) => {
              setKycDocs((p) => ({ ...p, [k]: v }));
              setKycErr(null);
            }}
            kycErr={kycErr}
            doKyc={doKyc}
            onRefresh={doRefreshSession}
            bankVals={bankVals}
            setBankVals={(v) => {
              setBankVals(v);
              setBankErr(null);
            }}
            bankErr={bankErr}
            bankNets={bankNets}
            netsLoading={netsLoading}
            bankResolved={bankResolved}
            rateLock={rateLock}
            doVerifyBank={doVerifyBank}
            doProceedToKyc={doProceedToKyc}
            doChangeAccount={doChangeAccount}
            actionLoading={actionLoading}
            error={error}
            isDark={isDark}
            c={c}
          />
        )}
      </div>

      {!loading && session && !isTerminalStatus && (
        <SummaryFooter
          quote={quote}
          session={session}
          quoteLoading={quoteLoading}
          summaryOpen={summaryOpen}
          setSummaryOpen={setSummaryOpen}
          isDark={isDark}
          c={c}
          createSession={createSession}
        />
      )}
    </div>
  );

  if (variant === "inline")
    return (
      <>
        <style>{GCSS}</style>
        {card}
      </>
    );

  return (
    <div style={c.backdrop} onClick={handleClose}>
      <style>{GCSS}</style>
      {card}
    </div>
  );
}

// ─── Dot progress ─────────────────────────────────────────────────────────────
function DotProgress({
  total,
  current,
  isDark,
}: {
  total: number;
  current: number;
  isDark: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          style={{
            width: i === current ? 18 : 6,
            height: 6,
            borderRadius: 3,
            background:
              i === current
                ? isDark
                  ? "#3d7eff"
                  : "#0066ff"
                : i < current
                  ? isDark
                    ? "rgba(61,126,255,.5)"
                    : "rgba(0,102,255,.3)"
                  : isDark
                    ? "#2a2d3a"
                    : "#e5e7eb",
            transition: "all .3s ease",
          }}
        />
      ))}
    </div>
  );
}

// ─── Step view ────────────────────────────────────────────────────────────────
function StepView({
  session,
  quote,
  kycReq,
  localKycStep,
  createSession,
  previewConfirmed,
  setPreviewConfirmed,
  estimatedRate,
  rateLoading,
  channels,
  channelsLoading,
  cryptoPrice,
  cryptoPriceLoading,
  email,
  setEmail,
  emailErr,
  doEmail,
  otpDigits,
  setOtpDigits,
  otpErr,
  doOtp,
  doResendOtp,
  kycDocs,
  setKycDocs,
  kycErr,
  doKyc,
  onRefresh,
  bankVals,
  setBankVals,
  bankErr,
  bankNets,
  netsLoading,
  bankResolved,
  rateLock,
  doVerifyBank,
  doProceedToKyc,
  doChangeAccount,
  actionLoading,
  error,
  isDark,
  c,
}: {
  session: SessionResponse;
  quote: QuoteResponse | null;
  kycReq: KycRequirement | null;
  localKycStep: "KYC_ID" | "KYC" | null;
  createSession?: CreateSessionRequest;
  previewConfirmed: boolean;
  setPreviewConfirmed: (v: boolean) => void;
  estimatedRate: RateResponse | null;
  rateLoading: boolean;
  channels: CryptoChannelsResponse | null;
  channelsLoading: boolean;
  cryptoPrice: number | null;
  cryptoPriceLoading: boolean;
  email: string;
  setEmail: (v: string) => void;
  emailErr: string | null;
  doEmail: () => void;
  otpDigits: string[];
  setOtpDigits: (d: string[]) => void;
  otpErr: string | null;
  doOtp: () => void;
  doResendOtp: () => void;
  kycDocs: Record<string, string>;
  setKycDocs: (k: string, v: string) => void;
  kycErr: string | null;
  doKyc: () => void;
  onRefresh: () => void;
  bankVals: { accountNumber: string; networkId: string };
  setBankVals: (v: { accountNumber: string; networkId: string }) => void;
  bankErr: string | null;
  bankNets: BankNetwork[];
  netsLoading: boolean;
  bankResolved: { accountName: string; bankName: string } | null;
  rateLock: number;
  doVerifyBank: () => void;
  doProceedToKyc: () => void;
  doChangeAccount: () => void;
  actionLoading: boolean;
  error: string | null;
  isDark: boolean;
  c: Theme;
}) {
  // Show preview if createSession is provided and not yet confirmed
  if (!previewConfirmed && createSession) {
    return (
      <div className="pp-slide">
        <PreviewStep
          createSession={createSession}
          estimatedRate={estimatedRate}
          rateLoading={rateLoading}
          channels={channels}
          channelsLoading={channelsLoading}
          cryptoPrice={cryptoPrice}
          cryptoPriceLoading={cryptoPriceLoading}
          onContinue={() => setPreviewConfirmed(true)}
          isDark={isDark}
          c={c}
        />
      </div>
    );
  }

  // Client-side flow determination based on state flags
  let currentStep: string;

  console.log("StepView - Determining current step:", {
    emailVerified: session.emailVerified,
    kycVerified: session.kycVerified,
    type: session.type,
    bankAccountProvided: session.bankAccountProvided,
    bankResolved: !!bankResolved,
    status: session.status,
    hasPaymentInstruction: !!session.paymentInstruction,
    hasCryptoInstruction: !!session.cryptoInstruction
  });

  if (!session.emailVerified) {
    // Need to collect and verify email
    currentStep = session.status === 'PENDING_OTP' ? 'VERIFY_OTP' : 'COLLECT_EMAIL';
  } else if (session.type === 'OFFRAMP' && (!session.bankAccountProvided || bankResolved)) {
    // Offramp: collect bank account after email verification
    // Stay on bank account step if showing confirmation (bankResolved is set)
    currentStep = 'BANK_ACCOUNT';
  } else if (!session.kycVerified) {
    // Need KYC verification - use local step if in KYC flow
    currentStep = localKycStep || 'KYC_ID';
  } else {
    // All verified, await payment
    currentStep = 'AWAIT_PAYMENT';
  }

  console.log("StepView - Current step determined:", currentStep);

  // Filter KYC requirements into ID fields vs profile fields
  const idFieldTypes = ['NIN', 'BVN', 'ID_TYPE', 'ID_NUMBER'];
  const allKycFields = kycReq?.type === 'field_based' ? (kycReq.fields || []) : [];
  const idFields = allKycFields.filter(f => idFieldTypes.includes(f.documentType));
  const profileFields = allKycFields.filter(f => !idFieldTypes.includes(f.documentType));

  return (
    <div className="pp-slide">
      {error && <div style={c.errorBanner}>{error}</div>}
      {currentStep === "COLLECT_EMAIL" && (
        <EmailStep
          email={email}
          setEmail={setEmail}
          err={emailErr}
          onSubmit={doEmail}
          loading={actionLoading}
          isDark={isDark}
          c={c}
        />
      )}
      {currentStep === "VERIFY_OTP" && (
        <OtpStep
          digits={otpDigits}
          setDigits={setOtpDigits}
          err={otpErr}
          onSubmit={doOtp}
          onResend={doResendOtp}
          loading={actionLoading}
          isDark={isDark}
          c={c}
        />
      )}
      {currentStep === "KYC_ID" && (
        <KycIdStep
          fields={idFields}
          docs={kycDocs}
          setDoc={setKycDocs}
          err={kycErr}
          onSubmit={doKyc}
          loading={actionLoading}
          isDark={isDark}
          c={c}
        />
      )}
      {currentStep === "KYC" && (
        <KycStep
          req={kycReq}
          fields={profileFields}
          docs={kycDocs}
          setDoc={setKycDocs}
          err={kycErr}
          onSubmit={doKyc}
          onRefresh={onRefresh}
          loading={actionLoading}
          isDark={isDark}
          c={c}
        />
      )}
      {currentStep === "BANK_ACCOUNT" && (
        <BankStep
          vals={bankVals}
          setVals={setBankVals}
          err={bankErr}
          nets={bankNets}
          netsLoading={netsLoading}
          resolved={bankResolved}
          rateLock={rateLock}
          onVerify={doVerifyBank}
          onConfirm={doProceedToKyc}
          onChangeAccount={doChangeAccount}
          loading={actionLoading}
          isDark={isDark}
          c={c}
        />
      )}
      {currentStep === "AWAIT_PAYMENT" &&
        (session.type === "OFFRAMP" && session.cryptoInstruction ? (
          <CryptoStep
            instruction={session.cryptoInstruction}
            isDark={isDark}
            c={c}
          />
        ) : session.paymentInstruction ? (
          <PaymentStep
            instruction={session.paymentInstruction}
            quote={quote}
            isDark={isDark}
            c={c}
          />
        ) : (
          <PendingStep message="Waiting for payment details…" c={c} />
        ))}
      {!currentStep && <PendingStep message="Preparing your session…" c={c} />}
    </div>
  );
}

// ─── Preview step ─────────────────────────────────────────────────────────────
function PreviewStep({
  createSession,
  estimatedRate,
  rateLoading,
  channels,
  channelsLoading,
  cryptoPrice,
  cryptoPriceLoading,
  onContinue,
  isDark,
  c,
}: {
  createSession: CreateSessionRequest;
  estimatedRate: RateResponse | null;
  rateLoading: boolean;
  channels: CryptoChannelsResponse | null;
  channelsLoading: boolean;
  cryptoPrice: number | null;
  cryptoPriceLoading: boolean;
  onContinue: () => void;
  isDark: boolean;
  c: Theme;
}) {
  const { type, cryptoAmount, cryptoCurrency, amount, currency, countryCode } = createSession;

  // Determine currency symbol
  const getCurrencySymbol = (curr: string) => {
    const symbols: Record<string, string> = {
      NGN: '\u20A6', // Naira
      USD: '$',
      GBP: '\u00A3',
      EUR: '\u20AC',
      KES: 'KSh',
      GHS: 'GH\u20B5',
      UGX: 'USh',
      ZAR: 'R',
    };
    return symbols[curr] || curr;
  };

  const currencySymbol = getCurrencySymbol(currency);

  const isStablecoin = ['USDT', 'USDC', 'DAI', 'USDD', 'BUSD'].includes(cryptoCurrency);

  // Calculate estimated amounts with REAL crypto prices
  let estimatedFiat = 0;
  let estimatedCryptoAmount = cryptoAmount || 0;

  if (estimatedRate && cryptoPrice) {
    if (type === 'ONRAMP') {
      // For onramp: User pays fiat to buy crypto
      // 1. Get fiat amount from createSession
      const fiatAmount = amount || 0;
      // 2. Convert fiat to USD using estimatedRate
      const usdAmount = estimatedRate.toAmount; // This is fiat converted to USD
      // 3. Calculate crypto amount: USD / crypto price
      estimatedCryptoAmount = usdAmount / cryptoPrice;
      // 4. Estimated fiat is what they pay
      estimatedFiat = fiatAmount;
    } else {
      // For offramp: User sells crypto for fiat
      // 1. Get crypto amount from createSession
      const cryptoAmt = cryptoAmount || 0;
      // 2. Calculate USD value: crypto amount × crypto price
      const usdValue = cryptoAmt * cryptoPrice;
      // 3. Convert USD to local currency using estimatedRate.rate
      // Since we fetched fiat→USD rate, we multiply USD by rate to get fiat
      // Example: if rate is 1400 (1 USD = 1400 NGN), then usdValue × 1400 = NGN
      estimatedFiat = usdValue * estimatedRate.rate;
      // 4. Crypto amount stays the same
      estimatedCryptoAmount = cryptoAmt;
    }
  }

  // Validation logic for min/max amounts
  let validationError: string | null = null;
  let minAmount: number | undefined;
  let maxAmount: number | undefined;

  if (channels && estimatedRate && estimatedFiat > 0) {
    // Find the channel for the crypto currency
    const channel = channels.channels.find(ch => ch.code === cryptoCurrency);

    if (channel) {
      if (type === 'ONRAMP') {
        minAmount = channel.buyMinLocal?.[currency];
        maxAmount = channel.buyMaxLocal?.[currency];
      } else {
        minAmount = channel.sellMinLocal?.[currency];
        maxAmount = channel.sellMaxLocal?.[currency];
      }

      // Validate the estimated fiat amount
      if (minAmount !== undefined && estimatedFiat < minAmount) {
        validationError = `Minimum ${type === 'ONRAMP' ? 'purchase' : 'sell'} amount is ${currencySymbol}${minAmount.toLocaleString()}`;
      } else if (maxAmount !== undefined && estimatedFiat > maxAmount) {
        validationError = `Maximum ${type === 'ONRAMP' ? 'purchase' : 'sell'} amount is ${currencySymbol}${maxAmount.toLocaleString()}`;
      }
    } else {
      console.warn(`Channel not found for crypto currency: ${cryptoCurrency}`);
    }
  }

  return (
    <div style={c.step}>
      <p style={c.stepTitle}>
        {type === 'ONRAMP' ? "You're buying" : "You're selling"}
      </p>
      <div style={{
        background: isDark ? '#1a1d27' : '#f7f8fa',
        borderRadius: 16,
        padding: '24px',
        marginTop: 8,
        marginBottom: 8,
      }}>
        <div style={{
          fontSize: 32,
          fontWeight: 600,
          color: isDark ? '#f0f1f5' : '#111',
          marginBottom: 4,
        }}>
          {estimatedCryptoAmount.toFixed(6)} {cryptoCurrency}
        </div>
        {rateLoading || cryptoPriceLoading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 12,
          }}>
            <Spin />
            <span style={{ fontSize: 14, color: isDark ? '#8892a4' : '#6b7280' }}>
              Fetching rate...
            </span>
          </div>
        ) : estimatedRate ? (
          <>
            <div style={{
              fontSize: 18,
              color: isDark ? '#8892a4' : '#6b7280',
              marginTop: 12,
            }}>
              {type === 'ONRAMP' ? 'Estimated cost:' : 'Estimated payout:'} ~{currencySymbol}
              {estimatedFiat.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </div>
            <div style={{
              fontSize: 13,
              color: isDark ? '#6b7280' : '#9ca3af',
              marginTop: 8,
            }}>
              (Rate: 1 {isStablecoin ? cryptoCurrency : 'USD'} = {currencySymbol}
              {estimatedRate.rate.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })})
            </div>
          </>
        ) : (
          <div style={{
            fontSize: 14,
            color: '#ef4444',
            marginTop: 12,
          }}>
            Unable to fetch rate
          </div>
        )}
      </div>
      {validationError && (
        <div style={{
          fontSize: 14,
          color: '#ef4444',
          marginTop: 12,
          textAlign: 'center',
        }}>
          ❌ {validationError}
        </div>
      )}
      {channelsLoading && !validationError && (
        <div style={{
          fontSize: 13,
          color: isDark ? '#8892a4' : '#6b7280',
          marginTop: 8,
          textAlign: 'center',
        }}>
          Validating amount...
        </div>
      )}
      <p style={{
        ...c.stepSub,
        textAlign: 'center',
        marginTop: 8,
        marginBottom: 8,
      }}>
        Final amount confirmed after verification
      </p>
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
        onClick={onContinue}
        disabled={rateLoading || cryptoPriceLoading || !estimatedRate || !cryptoPrice || !!validationError}
        style={validationError ? { cursor: 'not-allowed', opacity: 0.5 } : undefined}
      >
        Continue
      </button>
    </div>
  );
}

// ─── Email step ───────────────────────────────────────────────────────────────
function EmailStep({
  email,
  setEmail,
  err,
  onSubmit,
  loading,
  isDark,
  c,
}: {
  email: string;
  setEmail: (v: string) => void;
  err: string | null;
  onSubmit: () => void;
  loading: boolean;
  isDark: boolean;
  c: Theme;
}) {
  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Enter your email</p>
      <p style={c.stepSub}>
        We'll send a one-time code to verify your identity.
      </p>
      <input
        className={`pp-input${isDark ? " pp-input-dark" : ""}`}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSubmit()}
        placeholder="you@example.com"
        autoFocus
      />
      {err && <p style={c.inlineErr}>{err}</p>}
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
        onClick={onSubmit}
        disabled={!email || loading}
      >
        {loading ? (
          <>
            <Spin />
            Sending…
          </>
        ) : (
          "Continue"
        )}
      </button>
    </div>
  );
}

// ─── OTP step ─────────────────────────────────────────────────────────────────
function OtpStep({
  digits,
  setDigits,
  err,
  onSubmit,
  onResend,
  loading,
  isDark,
  c,
}: {
  digits: string[];
  setDigits: (d: string[]) => void;
  err: string | null;
  onSubmit: () => void;
  onResend: () => void;
  loading: boolean;
  isDark: boolean;
  c: Theme;
}) {
  const [secs, setSecs] = useState(30);
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setSecs(30);
    setTimeout(() => refs.current[0]?.focus(), 80);
  }, []);
  useEffect(() => {
    if (secs <= 0) return;
    const t = setInterval(() => setSecs((p) => p - 1), 1000);
    return () => clearInterval(t);
  }, [secs]);

  function handleChange(i: number, val: string) {
    const n = val.replace(/\D/g, "");
    if (n.length > 1) {
      const next = [...digits];
      for (let j = 0; j < n.length && i + j < OTP_LEN; j++) next[i + j] = n[j];
      setDigits(next);
      refs.current[Math.min(i + n.length, OTP_LEN - 1)]?.focus();
      return;
    }
    const next = [...digits];
    next[i] = n;
    setDigits(next);
    if (n && i < OTP_LEN - 1) refs.current[i + 1]?.focus();
  }

  function handleKey(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0)
      refs.current[i - 1]?.focus();
    if (e.key === "Enter") onSubmit();
  }

  async function handleResend() {
    await onResend();
    setSecs(30);
  }

  const filled = digits.join("").length === OTP_LEN;

  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Check your email</p>
      <p style={c.stepSub}>Enter the 6-digit code we sent you.</p>
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "center",
          margin: "4px 0",
        }}
      >
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            className={`pp-otp-inp${isDark ? " pp-otp-inp-dark" : ""}${d ? " filled" : ""}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKey(i, e)}
            disabled={loading}
          />
        ))}
      </div>
      {err && <p style={c.inlineErr}>{err}</p>}
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
        onClick={onSubmit}
        disabled={!filled || loading}
      >
        {loading ? (
          <>
            <Spin />
            Verifying…
          </>
        ) : (
          "Verify code"
        )}
      </button>
      <div style={{ textAlign: "center" }}>
        <button
          className="pp-link"
          style={{ color: isDark ? "#8892a4" : "#6b7280" }}
          onClick={handleResend}
          disabled={secs > 0 || loading}
        >
          {secs > 0 ? `Resend code in ${secs}s` : "Resend code"}
        </button>
      </div>
    </div>
  );
}

// ─── Phone Input Component ────────────────────────────────────────────────────
function PhoneInput({
  value,
  onChange,
  isDark,
  c,
}: {
  value: string;
  onChange: (v: string) => void;
  isDark: boolean;
  c: Theme;
}) {
  // Parse existing value to extract country code and number
  const parsePhone = (phone: string) => {
    if (!phone) return { countryCode: "+234", number: "" };

    // Find matching country code
    const matchedCode = COUNTRY_CODES.find(cc => phone.startsWith(cc.code));
    if (matchedCode) {
      return {
        countryCode: matchedCode.code,
        number: phone.substring(matchedCode.code.length)
      };
    }

    // Default to Nigeria if no match
    return { countryCode: "+234", number: phone.replace(/^\+/, "") };
  };

  const { countryCode: initialCode, number: initialNumber } = parsePhone(value);
  const [countryCode, setCountryCode] = useState(initialCode);
  const [phoneNumber, setPhoneNumber] = useState(initialNumber);

  const handleCountryChange = (code: string) => {
    setCountryCode(code);
    onChange(code + phoneNumber);
  };

  const handleNumberChange = (num: string) => {
    // Remove any non-digit characters
    const cleaned = num.replace(/\D/g, "");
    setPhoneNumber(cleaned);
    onChange(countryCode + cleaned);
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <select
        className={`pp-input${isDark ? " pp-input-dark" : ""}`}
        value={countryCode}
        onChange={(e) => handleCountryChange(e.target.value)}
        style={{ cursor: "pointer", minWidth: 120, flex: "0 0 auto" }}
      >
        {COUNTRY_CODES.map((cc) => (
          <option key={cc.code} value={cc.code}>
            {cc.flag} {cc.code}
          </option>
        ))}
      </select>
      <input
        className={`pp-input${isDark ? " pp-input-dark" : ""}`}
        type="tel"
        value={phoneNumber}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder="8012345678"
        style={{ flex: 1 }}
      />
    </div>
  );
}

// ─── KYC ID step (Step 1: ID verification) ───────────────────────────────────
function KycIdStep({
  fields,
  docs,
  setDoc,
  err,
  onSubmit,
  loading,
  isDark,
  c,
}: {
  fields: KycField[];
  docs: Record<string, string>;
  setDoc: (k: string, v: string) => void;
  err: string | null;
  onSubmit: () => void;
  loading: boolean;
  isDark: boolean;
  c: Theme;
}) {
  if (fields.length === 0) {
    return (
      <div style={c.step}>
        <p style={c.stepTitle}>Verify your identity - Step 1</p>
        <p style={c.inlineErr}>No ID requirements found</p>
      </div>
    );
  }

  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Verify your identity - Step 1</p>
      <p style={c.stepSub}>
        Enter your government ID details. This helps us verify your identity securely.
      </p>
      <div
        style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}
      >
        {fields.map((field) => (
          <div key={field.documentType}>
            <p style={c.inputLabel}>
              {field.label}
              {field.required && <span style={{ color: "red" }}> *</span>}
            </p>
            <input
              className={`pp-input${isDark ? " pp-input-dark" : ""}`}
              type="text"
              value={docs[field.documentType] ?? ""}
              onChange={(e) => setDoc(field.documentType, e.target.value)}
              placeholder={kycPlaceholder(field.documentType)}
            />
          </div>
        ))}
      </div>
      {err && <p style={c.inlineErr}>{err}</p>}
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
        onClick={onSubmit}
        disabled={loading}
      >
        {loading ? (
          <>
            <Spin />
            Submitting…
          </>
        ) : (
          "Continue"
        )}
      </button>
    </div>
  );
}

// ─── KYC step (Step 2: Profile information) ───────────────────────────────────
function KycStep({
  req,
  fields,
  docs,
  setDoc,
  err,
  onSubmit,
  onRefresh,
  loading,
  isDark,
  c,
}: {
  req: KycRequirement | null;
  fields: KycField[];
  docs: Record<string, string>;
  setDoc: (k: string, v: string) => void;
  err: string | null;
  onSubmit: () => void;
  onRefresh: () => void;
  loading: boolean;
  isDark: boolean;
  c: Theme;
}) {
  if (!req) return <PendingStep message="Loading requirements…" c={c} />;
  if (req.type === "redirect")
    return (
      <div style={c.step}>
        <p style={c.stepTitle}>Verify identity</p>
        <p style={c.stepSub}>Complete KYC in the secure provider window.</p>
        <a
          href={req.redirectUrl}
          target="_blank"
          rel="noreferrer"
          className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
          style={{ textDecoration: "none" }}
        >
          Open verification
        </a>
        <button
          className={`pp-btn ${isDark ? "pp-btn-dark-ghost" : "pp-btn-ghost"}`}
          onClick={onRefresh}
          style={{ marginTop: 4 }}
        >
          I have completed verification
        </button>
      </div>
    );
  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Verify your identity - Step 2</p>
      <p style={c.stepSub}>
        Complete your profile information. Almost done!
      </p>
      <div
        style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}
      >
        {fields.map((f) => (
          <div key={f.documentType}>
            <p style={c.inputLabel}>
              {f.label}
              {f.required && <span style={{ color: "red" }}> *</span>}
            </p>
            {f.documentType === "PHONE" ? (
              <PhoneInput
                value={docs[f.documentType] ?? ""}
                onChange={(v) => setDoc(f.documentType, v)}
                isDark={isDark}
                c={c}
              />
            ) : (
              <input
                className={`pp-input${isDark ? " pp-input-dark" : ""}`}
                type={
                  f.documentType === "EMAIL"
                    ? "email"
                    : f.documentType === "DOB"
                      ? "date"
                      : "text"
                }
                value={docs[f.documentType] ?? ""}
                onChange={(e) => setDoc(f.documentType, e.target.value)}
                placeholder={kycPlaceholder(f.documentType)}
              />
            )}
          </div>
        ))}
      </div>
      {err && <p style={c.inlineErr}>{err}</p>}
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
        onClick={onSubmit}
        disabled={loading}
      >
        {loading ? (
          <>
            <Spin />
            Submitting…
          </>
        ) : (
          "Submit"
        )}
      </button>
    </div>
  );
}

// ─── Bank step ────────────────────────────────────────────────────────────────
function BankStep({
  vals,
  setVals,
  err,
  nets,
  netsLoading,
  resolved,
  rateLock,
  onVerify,
  onConfirm,
  onChangeAccount,
  loading,
  isDark,
  c,
}: {
  vals: { accountNumber: string; networkId: string };
  setVals: (v: { accountNumber: string; networkId: string }) => void;
  err: string | null;
  nets: BankNetwork[];
  netsLoading: boolean;
  resolved: { accountName: string; bankName: string } | null;
  rateLock: number;
  onVerify: () => void;
  onConfirm: () => void;
  onChangeAccount: () => void;
  loading: boolean;
  isDark: boolean;
  c: Theme;
}) {
  const mins = Math.floor(rateLock / 60),
    secs = rateLock % 60;
  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Bank account</p>
      <p style={c.stepSub}>Enter the account to receive your payout.</p>
      <div
        style={{ display: "flex", flexDirection: "column" as const, gap: 10 }}
      >
        <div>
          <p style={c.inputLabel}>Bank</p>
          <select
            className={`pp-input${isDark ? " pp-input-dark" : ""}`}
            value={vals.networkId}
            onChange={(e) => setVals({ ...vals, networkId: e.target.value })}
            disabled={netsLoading}
            style={{ cursor: "pointer" }}
          >
            <option value="">Select your bank</option>
            {nets.map((n) => (
              <option key={n.id} value={n.id}>
                {n.code ? `${n.name} (${n.code})` : n.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p style={c.inputLabel}>Account number</p>
          <input
            className={`pp-input${isDark ? " pp-input-dark" : ""}`}
            type="text"
            value={vals.accountNumber}
            onChange={(e) =>
              setVals({ ...vals, accountNumber: e.target.value })
            }
            placeholder="0123456789"
            maxLength={10}
          />
        </div>
      </div>
      {err && <p style={c.inlineErr}>{err}</p>}
      {!resolved && (
        <button
          className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
          onClick={onVerify}
          disabled={loading || !vals.accountNumber || !vals.networkId}
        >
          {loading ? (
            <>
              <Spin />
              Verifying…
            </>
          ) : (
            "Verify account"
          )}
        </button>
      )}
      {resolved && (
        <div style={c.resolvedBox}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={c.resolvedIcon}>✓</div>
            <div>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: isDark ? "#f0f1f5" : "#111",
                  margin: 0,
                }}
              >
                {resolved.accountName}
              </p>
              <p
                style={{
                  fontSize: 13,
                  color: isDark ? "#8892a4" : "#6b7280",
                  margin: 0,
                }}
              >
                {resolved.bankName}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button
              className={`pp-btn ${isDark ? "pp-btn-dark-secondary" : "pp-btn-secondary"}`}
              onClick={onChangeAccount}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Change account
            </button>
            <button
              className={`pp-btn ${isDark ? "pp-btn-dark-primary" : "pp-btn-primary"}`}
              onClick={onConfirm}
              disabled={loading}
              style={{ flex: 1 }}
            >
              Continue
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Payment step ─────────────────────────────────────────────────────────────
function PaymentStep({
  instruction,
  quote,
  isDark,
  c,
}: {
  instruction: {
    accountNumber: string;
    accountName: string;
    bankName: string;
    amountLocal?: number;
    localCurrency?: string;
    cryptoAmount?: number;
    cryptoCurrency?: string;
    expiresAt?: string | null;
  };
  quote: QuoteResponse | null;
  isDark: boolean;
  c: Theme;
}) {
  const [copied, setCopied] = useState(false);
  function copy(v: string) {
    navigator.clipboard.writeText(v).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Make a bank transfer</p>
      <p style={c.stepSub}>
        Send the exact amount to the account below. Your crypto will be released
        automatically.
      </p>

      {/* Prominent amount display */}
      {instruction.amountLocal && instruction.localCurrency && (
        <div style={{
          textAlign: 'center',
          padding: '16px 0',
          margin: '8px 0',
        }}>
          <p style={{
            fontSize: 13,
            fontWeight: 500,
            color: isDark ? '#8892a4' : '#6b7280',
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>Amount to Send</p>
          <p style={{
            fontSize: 42,
            fontWeight: 700,
            color: isDark ? '#f0f1f5' : '#111',
            margin: 0,
            fontFamily: "'DM Mono', monospace",
            lineHeight: 1.2,
          }}>
            {instruction.localCurrency} {instruction.amountLocal.toLocaleString()}
          </p>
        </div>
      )}

      <div style={c.infoBox}>
        {(instruction.cryptoAmount || quote?.cryptoAmount) && (instruction.cryptoCurrency || quote?.cryptoCurrency) && (
          <InfoRow
            label="You get"
            value={`${Number(instruction.cryptoAmount || quote?.cryptoAmount || 0).toFixed(4)} ${instruction.cryptoCurrency || quote?.cryptoCurrency || 'USDC'}`}
            mono
            highlight
            isDark={isDark}
            c={c}
          />
        )}
        <InfoRow
          label="Account number"
          value={instruction.accountNumber}
          mono
          isDark={isDark}
          c={c}
          action={
            <button
              className="pp-link"
              style={{ color: isDark ? "#3d7eff" : "#0066ff", fontSize: 13 }}
              onClick={() => copy(instruction.accountNumber)}
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          }
        />
        <InfoRow
          label="Account name"
          value={instruction.accountName}
          isDark={isDark}
          c={c}
        />
        <InfoRow
          label="Bank"
          value={instruction.bankName}
          isDark={isDark}
          c={c}
        />
        {instruction.expiresAt && (
          <p
            style={{
              fontSize: 12,
              color: isDark ? "#4a4f6a" : "#9ca3af",
              marginTop: 4,
            }}
          >
            Expires {new Date(instruction.expiresAt).toLocaleString()}
          </p>
        )}
      </div>
      <div style={c.pendingRow}>
        <Spin />
        <span style={{ color: isDark ? "#8892a4" : "#6b7280", fontSize: 14 }}>
          Waiting for payment…
        </span>
      </div>
    </div>
  );
}

// ─── Crypto step ──────────────────────────────────────────────────────────────
function CryptoStep({
  instruction,
  isDark,
  c,
}: {
  instruction: {
    walletAddress: string;
    cryptoNetwork: string;
    cryptoCurrency: string;
    cryptoAmount: number;
    amountLocal?: number;
    localCurrency?: string;
    expiresAt?: string | null;
  };
  isDark: boolean;
  c: Theme;
}) {
  const [copied, setCopied] = useState(false);
  const [qrCode, setQrCode] = useState<string>("");

  useEffect(() => {
    QRCode.toDataURL(instruction.walletAddress, {
      width: 200,
      margin: 2,
      color: {
        dark: isDark ? "#ffffff" : "#000000",
        light: isDark ? "#1a1d2e" : "#ffffff",
      },
    })
      .then(setQrCode)
      .catch((err) => console.error("QR code generation failed:", err));
  }, [instruction.walletAddress, isDark]);

  function copy(v: string) {
    navigator.clipboard.writeText(v).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={c.step}>
      <p style={c.stepTitle}>Send crypto</p>
      <p style={c.stepSub}>
        Send exactly the amount below to receive your{" "}
        {instruction.amountLocal && instruction.localCurrency
          ? `${instruction.localCurrency} ${instruction.amountLocal.toLocaleString()}`
          : "NGN"}{" "}
        payout.
      </p>

      {/* QR Code */}
      {qrCode && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            margin: "16px 0",
          }}
        >
          <img
            src={qrCode}
            alt="Wallet Address QR Code"
            style={{
              width: 200,
              height: 200,
              border: isDark ? "2px solid #2a2d3e" : "2px solid #e5e7eb",
              borderRadius: 8,
            }}
          />
        </div>
      )}

      <div style={c.infoBox}>
        <div style={{ marginBottom: 8 }}>
          <p style={c.infoLabel}>Wallet address</p>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <p
              style={{
                ...c.infoMono,
                fontSize: 12,
                wordBreak: "break-all",
                flex: 1,
              }}
            >
              {instruction.walletAddress}
            </p>
            <button
              className="pp-link"
              style={{
                color: isDark ? "#3d7eff" : "#0066ff",
                fontSize: 13,
                flexShrink: 0,
              }}
              onClick={() => copy(instruction.walletAddress)}
            >
              {copied ? "✓" : "Copy"}
            </button>
          </div>
        </div>
        <InfoRow
          label="Network"
          value={instruction.cryptoNetwork}
          isDark={isDark}
          c={c}
        />
        <InfoRow
          label="Amount"
          value={`${instruction.cryptoAmount} ${instruction.cryptoCurrency}`}
          mono
          highlight
          isDark={isDark}
          c={c}
        />
        {instruction.amountLocal && instruction.localCurrency && (
          <InfoRow
            label="You'll receive"
            value={`${instruction.localCurrency} ${instruction.amountLocal.toLocaleString()}`}
            mono
            highlight
            isDark={isDark}
            c={c}
          />
        )}
        {instruction.expiresAt && (
          <p
            style={{
              fontSize: 12,
              color: isDark ? "#4a4f6a" : "#9ca3af",
              marginTop: 4,
            }}
          >
            Expires {new Date(instruction.expiresAt).toLocaleString()}
          </p>
        )}
      </div>
      <div style={c.pendingRow}>
        <Spin />
        <span style={{ color: isDark ? "#8892a4" : "#6b7280", fontSize: 14 }}>
          Waiting for transfer…
        </span>
      </div>
    </div>
  );
}

// ─── Pending ──────────────────────────────────────────────────────────────────
function PendingStep({ message, c }: { message: string; c: Theme }) {
  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "32px 0",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Spin large />
      </div>
      <p style={{ ...c.stepSub, margin: 0 }}>{message}</p>
    </div>
  );
}

// ─── Success ──────────────────────────────────────────────────────────────────
function SuccessScreen({
  session,
  quote,
  isDark,
  c,
  onClose,
}: {
  session: SessionResponse;
  quote: QuoteResponse | null;
  isDark: boolean;
  c: Theme;
  onClose?: () => void;
}) {
  const isSell = session.type === "OFFRAMP";

  // Get crypto amount and currency from available sources
  const cryptoAmount =
    quote?.cryptoAmount ??
    session.paymentInstruction?.cryptoAmount ??
    session.cryptoInstruction?.cryptoAmount ??
    session.transaction?.cryptoAmount ??
    0;

  const cryptoCurrency =
    quote?.cryptoCurrency ??
    session.paymentInstruction?.cryptoCurrency ??
    session.cryptoInstruction?.cryptoCurrency ??
    session.transaction?.cryptoCurrency ??
    "USDT";

  const amountLocal =
    quote?.amountFiat ??
    session.paymentInstruction?.amountLocal ??
    session.cryptoInstruction?.amountLocal ??
    session.transaction?.amountLocal ??
    0;

  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "8px 0 16px",
      }}
    >
      <div style={c.successRing}>
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path
            d="M6 14l6 6 10-10"
            stroke={isDark ? "#3d7eff" : "#0066ff"}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p style={{ ...c.stepTitle, marginBottom: 6 }}>
        {isSell ? "Payout sent!" : "Crypto on the way!"}
      </p>
      <p style={{ ...c.stepSub, maxWidth: 260 }}>
        {isSell
          ? "Your NGN has been sent to your bank account."
          : `${cryptoAmount} ${cryptoCurrency} is being sent to your wallet.`}
      </p>
      <div style={{ ...c.infoBox, width: "100%", marginTop: 4 }}>
        <InfoRow
          label="Amount"
          value={`NGN ${Number(amountLocal).toLocaleString()}`}
          isDark={isDark}
          c={c}
        />
        <InfoRow
          label="You receive"
          value={`${cryptoAmount} ${cryptoCurrency}`}
          mono
          highlight
          isDark={isDark}
          c={c}
        />
        {session.txHash && (
          <InfoRow
            label="Transaction"
            value={`${session.txHash.slice(0, 6)}...${session.txHash.slice(-4)}`}
            mono
            isDark={isDark}
            c={c}
            action={
              <button
                className="pp-link"
                style={{ color: isDark ? "#3d7eff" : "#0066ff", fontSize: 13 }}
                onClick={() => {
                  const hash = session.txHash;
                  if (hash) {
                    navigator.clipboard.writeText(hash).catch(() => {});
                  }
                }}
              >
                Copy
              </button>
            }
          />
        )}
      </div>
      <button
        className={`pp-btn ${isDark ? "pp-btn-dark-ghost" : "pp-btn-ghost"}`}
        onClick={onClose}
        style={{ marginTop: 4 }}
      >
        Close
      </button>
    </div>
  );
}

// ─── Payout Pending ───────────────────────────────────────────────────────────
function PayoutPendingScreen({
  session,
  quote,
  isDark,
  c,
}: {
  session: SessionResponse;
  quote: QuoteResponse | null;
  isDark: boolean;
  c: Theme;
}) {
  const cryptoAmount =
    session.cryptoInstruction?.cryptoAmount ??
    quote?.cryptoAmount ??
    0;
  const cryptoCurrency =
    session.cryptoInstruction?.cryptoCurrency ??
    quote?.cryptoCurrency ??
    "USDT";
  const amountLocal =
    session.cryptoInstruction?.amountLocal ??
    quote?.amountFiat ??
    0;

  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "8px 0 16px",
      }}
    >
      <div style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: isDark ? "rgba(245,158,11,.1)" : "#fef3c7",
        border: isDark ? "2px solid rgba(245,158,11,.3)" : "2px solid #fcd34d",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke="#f59e0b"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <p style={{ ...c.stepTitle, marginBottom: 6 }}>
        Crypto received!
      </p>
      <p style={{ ...c.stepSub, maxWidth: 260 }}>
        We've received your {cryptoAmount} {cryptoCurrency}. Your payout is being prepared.
      </p>
      <div style={{ ...c.infoBox, width: "100%", marginTop: 4 }}>
        <InfoRow
          label="Crypto received"
          value={`${cryptoAmount} ${cryptoCurrency}`}
          mono
          highlight
          isDark={isDark}
          c={c}
        />
        <InfoRow
          label="Payout amount"
          value={`NGN ${Number(amountLocal).toLocaleString()}`}
          isDark={isDark}
          c={c}
        />
      </div>
      <div style={c.pendingRow}>
        <Spin />
        <span style={{ color: isDark ? "#8892a4" : "#6b7280", fontSize: 14 }}>
          Preparing your payout…
        </span>
      </div>
    </div>
  );
}

// ─── Payout Processing ────────────────────────────────────────────────────────
function PayoutProcessingScreen({
  session,
  quote,
  isDark,
  c,
}: {
  session: SessionResponse;
  quote: QuoteResponse | null;
  isDark: boolean;
  c: Theme;
}) {
  const cryptoAmount =
    session.cryptoInstruction?.cryptoAmount ??
    quote?.cryptoAmount ??
    0;
  const cryptoCurrency =
    session.cryptoInstruction?.cryptoCurrency ??
    quote?.cryptoCurrency ??
    "USDT";
  const amountLocal =
    session.cryptoInstruction?.amountLocal ??
    quote?.amountFiat ??
    0;

  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "8px 0 16px",
      }}
    >
      <div style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: isDark ? "rgba(59,130,246,.1)" : "#dbeafe",
        border: isDark ? "2px solid rgba(59,130,246,.3)" : "2px solid #93c5fd",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16
      }}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
          <path
            d="M13 10V3L4 14h7v7l9-11h-7z"
            fill={isDark ? "#3b82f6" : "#2563eb"}
            opacity="0.8"
          />
        </svg>
      </div>
      <p style={{ ...c.stepTitle, marginBottom: 6 }}>
        Processing payout
      </p>
      <p style={{ ...c.stepSub, maxWidth: 260 }}>
        We're sending NGN {Number(amountLocal).toLocaleString()} to your bank account. This usually takes a few minutes.
      </p>
      <div style={{ ...c.infoBox, width: "100%", marginTop: 4 }}>
        <InfoRow
          label="Crypto received"
          value={`${cryptoAmount} ${cryptoCurrency}`}
          mono
          isDark={isDark}
          c={c}
        />
        <InfoRow
          label="Payout amount"
          value={`NGN ${Number(amountLocal).toLocaleString()}`}
          highlight
          isDark={isDark}
          c={c}
        />
      </div>
      <div style={c.pendingRow}>
        <Spin />
        <span style={{ color: isDark ? "#8892a4" : "#6b7280", fontSize: 14 }}>
          Sending to your bank account…
        </span>
      </div>
    </div>
  );
}

// ─── Fail ─────────────────────────────────────────────────────────────────────
function FailScreen({ session, c }: { session: SessionResponse; c: Theme }) {
  // Check if this is a KYC validation error from metadata
  const metadata = (session as any).metadata || {};
  const isKycValidationError = metadata.kycValidationError === true;
  const errorMessage = metadata.errorMessage || '';
  const errorFields = metadata.errorFields || [];

  // Legacy support: check for kycError object
  const kycError = (session as any).kycError;

  if (isKycValidationError || kycError) {
    return (
      <div
        style={{
          ...c.step,
          alignItems: "center",
          textAlign: "center",
          padding: "8px 0",
        }}
      >
        <div style={c.failRing}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              stroke="#f59e0b"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <p style={c.stepTitle}>Validation Error</p>
        <p style={c.stepSub}>
          There was an issue with the information provided. Please check the details and try again.
        </p>
        {(errorMessage || kycError?.message) && (
          <p style={{ ...c.stepSub, fontSize: 13, marginTop: 8, color: "#ef4444" }}>
            {(errorMessage || kycError?.message || '').replace('KYC validation error: ', '')}
          </p>
        )}
        {errorFields.length > 0 && (
          <p style={{ ...c.stepSub, fontSize: 12, marginTop: 4, color: "#6b7280" }}>
            Fields with issues: {errorFields.join(', ')}
          </p>
        )}
        {kycError?.userPortalUrl && (
          <button
            onClick={() => {
              // Open user portal in new tab with session context
              const portalUrl = new URL(kycError.userPortalUrl);
              portalUrl.searchParams.set('sessionId', session.id);
              portalUrl.searchParams.set('error', 'kyc');
              if (kycError.fields && kycError.fields.length > 0) {
                portalUrl.searchParams.set('fields', kycError.fields.join(','));
              }
              window.open(portalUrl.toString(), '_blank');
            }}
            style={{
              ...c.btn,
              marginTop: 16,
              width: '100%',
              maxWidth: 280,
            }}
          >
            Update KYC Details
          </button>
        )}
      </div>
    );
  }

  // Default failure screen
  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "8px 0",
      }}
    >
      <div style={c.failRing}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path
            d="M5 5l14 14M19 5L5 19"
            stroke="#ef4444"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </div>
      <p style={c.stepTitle}>Session ended</p>
      <p style={c.stepSub}>
        Something went wrong. Please try again.
      </p>
    </div>
  );
}

// ─── Expired ──────────────────────────────────────────────────────────────────
function ExpiredScreen({ session, c, isDark }: { session: SessionResponse; c: Theme; isDark: boolean }) {
  return (
    <div
      style={{
        ...c.step,
        alignItems: "center",
        textAlign: "center",
        padding: "8px 0",
      }}
    >
      <div style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: isDark ? "rgba(245,158,11,.1)" : "#fef3c7",
        border: isDark ? "2px solid rgba(245,158,11,.3)" : "2px solid #fcd34d",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="#f59e0b" strokeWidth="2" />
          <path d="M12 6v6l4 2" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <p style={c.stepTitle}>Session expired</p>
      <p style={c.stepSub}>
        This session has expired. If you've already made the payment, we're checking for updates...
      </p>
      <div style={c.pendingRow}>
        <Spin />
        <span style={{ color: isDark ? "#8892a4" : "#6b7280", fontSize: 14 }}>
          Checking for late payment...
        </span>
      </div>
    </div>
  );
}

// ─── Loading ──────────────────────────────────────────────────────────────────
function LoadingScreen({ isDark, c }: { isDark: boolean; c: Theme }) {
  return (
    <div style={c.step}>
      {[100, 60, 80].map((w, i) => (
        <div
          key={i}
          style={{
            height: 14,
            borderRadius: 7,
            width: `${w}%`,
            background: isDark
              ? "linear-gradient(90deg,#1a1d27 25%,#2a2d3a 50%,#1a1d27 75%)"
              : "linear-gradient(90deg,#f0f2f5 25%,#e2e5ea 50%,#f0f2f5 75%)",
            backgroundSize: "200% 100%",
            animation: "ppShimmer 1.4s infinite",
            marginBottom: 6,
          }}
        />
      ))}
      <div
        style={{
          height: 44,
          borderRadius: 12,
          background: isDark
            ? "linear-gradient(90deg,#1a1d27 25%,#2a2d3a 50%,#1a1d27 75%)"
            : "linear-gradient(90deg,#f0f2f5 25%,#e2e5ea 50%,#f0f2f5 75%)",
          backgroundSize: "200% 100%",
          animation: "ppShimmer 1.4s infinite .1s",
          marginTop: 8,
        }}
      />
    </div>
  );
}

function ErrorScreen({ message, c }: { message: string; c: Theme }) {
  return (
    <div style={c.step}>
      <p style={{ color: "#ef4444", fontSize: 14 }}>{message}</p>
    </div>
  );
}

// ─── Summary footer ───────────────────────────────────────────────────────────
function SummaryFooter({
  quote,
  session,
  quoteLoading,
  summaryOpen,
  setSummaryOpen,
  isDark,
  c,
  createSession,
}: {
  quote: QuoteResponse | null;
  session: SessionResponse;
  quoteLoading: boolean;
  summaryOpen: boolean;
  setSummaryOpen: (v: (o: boolean) => boolean) => void;
  isDark: boolean;
  c: Theme;
  createSession?: CreateSessionRequest;
}) {
  const fiat = quote?.fiatCurrency ?? "NGN";
  const crypto =
    quote?.cryptoCurrency ?? createSession?.cryptoCurrency ?? "USDT";
  const amtFiat =
    quote?.amountFiat ??
    session?.transaction?.amountLocal ??
    createSession?.amountFiat ??
    createSession?.amountNgn ??
    0;
  const amtCrypto = quote?.cryptoAmount ?? createSession?.cryptoAmount ?? 0;
  const isSell = session?.type === "OFFRAMP";

  if (quoteLoading && !quote)
    return (
      <div style={c.summaryFooter}>
        <div style={{ padding: "14px 24px", display: "flex", gap: 8 }}>
          {[70, 50].map((w, i) => (
            <div
              key={i}
              style={{
                height: 12,
                borderRadius: 6,
                width: `${w}%`,
                background: isDark
                  ? "linear-gradient(90deg,#1a1d27 25%,#2a2d3a 50%,#1a1d27 75%)"
                  : "linear-gradient(90deg,#f0f2f5 25%,#e2e5ea 50%,#f0f2f5 75%)",
                backgroundSize: "200% 100%",
                animation: "ppShimmer 1.4s infinite",
              }}
            />
          ))}
        </div>
      </div>
    );

  return (
    <div style={c.summaryFooter}>
      <button style={c.summaryToggle} onClick={() => setSummaryOpen((o) => !o)}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: isDark ? "#f0f1f5" : "#111",
          }}
        >
          {isSell
            ? `${amtCrypto} ${crypto}`
            : `${fiat} ${amtFiat?.toLocaleString()}`}
          <span
            style={{ color: isDark ? "#4a4f6a" : "#9ca3af", margin: "0 6px" }}
          >
            →
          </span>
          {isSell
            ? `≈ NGN ${(quote?.estimatedFiat ?? 0).toLocaleString()}`
            : `≈ ${quote?.estimatedCrypto?.toFixed(4) ?? amtCrypto} ${crypto}`}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          style={{
            transform: summaryOpen ? "rotate(180deg)" : "none",
            transition: "transform .2s",
            color: isDark ? "#4a4f6a" : "#9ca3af",
            flexShrink: 0,
          }}
        >
          <path
            d="M2 4.5l5 5 5-5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {summaryOpen && (
        <div style={c.summaryBody} className="pp-slide">
          {(
            [
              [
                "Rate",
                isSell
                  ? quote?.sellRate?.toLocaleString()
                  : quote?.buyRate?.toLocaleString(),
                false,
              ],
              [
                "Fee",
                quote?.feeFiat
                  ? `${fiat} ${quote.feeFiat.toFixed(2)} (${quote.feePercent}%)`
                  : null,
                false,
              ],
              [
                isSell ? "You receive" : "You get",
                isSell
                  ? `${fiat} ${(quote?.estimatedFiat ?? 0).toLocaleString()}`
                  : `${((quote?.estimatedCrypto ?? amtCrypto) as number).toFixed(4)} ${crypto}`,
                true,
              ],
            ] as [string, string | null | undefined, boolean][]
          )
            .filter((r) => r[1])
            .map(([label, value, bold]) => (
              <div key={label} style={c.summaryRow}>
                <span
                  style={{
                    color: isDark ? "#8892a4" : "#6b7280",
                    fontSize: 13,
                  }}
                >
                  {label}
                </span>
                <span
                  style={{
                    color: isDark
                      ? bold
                        ? "#f0f1f5"
                        : "#c8cdd8"
                      : bold
                        ? "#111"
                        : "#374151",
                    fontSize: 13,
                    fontWeight: bold ? 600 : 400,
                    fontFamily: bold ? "'DM Mono',monospace" : "inherit",
                  }}
                >
                  {value}
                </span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────
function InfoRow({
  label,
  value,
  mono = false,
  highlight = false,
  action,
  isDark,
  c,
}: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: boolean;
  action?: React.ReactNode;
  isDark: boolean;
  c: Theme;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "7px 0",
        borderBottom: `1px solid ${isDark ? "#1e2130" : "#f3f4f6"}`,
      }}
    >
      <span style={c.infoLabel}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: highlight ? 600 : 500,
            fontFamily: mono ? "'DM Mono',monospace" : "inherit",
            color: highlight
              ? isDark
                ? "#3d7eff"
                : "#0066ff"
              : isDark
                ? "#f0f1f5"
                : "#111",
          }}
        >
          {value}
        </span>
        {action}
      </div>
    </div>
  );
}

function Spin({ large = false }: { large?: boolean }) {
  const s = large ? 24 : 16;
  return (
    <span
      style={{
        display: "inline-block",
        width: s,
        height: s,
        border: "2px solid rgba(0,0,0,.08)",
        borderTopColor: "#0066ff",
        borderRadius: "50%",
        animation: "ppSpin .7s linear infinite",
        flexShrink: 0,
      }}
    />
  );
}

// ─── Theme tokens ─────────────────────────────────────────────────────────────
const light: Theme = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    animation: "ppBackdrop .2s ease",
    padding: 16,
  },
  modal: {
    width: "100%",
    maxWidth: 420,
    background: "#fff",
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 32px 80px rgba(0,0,0,.25), 0 0 0 1px rgba(0,0,0,.06)",
    animation: "ppFadeIn .25s ease",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px 24px 0",
  },
  headerLeft: { display: "flex", flexDirection: "column", gap: 10 },
  logo: { display: "flex", alignItems: "center", gap: 7 },
  logoText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111",
    letterSpacing: "-.01em",
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    border: "1.5px solid #eaecf0",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#9ca3af",
    transition: "all .15s",
    flexShrink: 0,
    position: "relative",
    zIndex: 10,
  },
  body: { padding: "20px 24px 0", flex: 1 },
  step: { display: "flex", flexDirection: "column", gap: 14 },
  stepTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: "#111",
    letterSpacing: "-.02em",
    margin: 0,
    lineHeight: 1.2,
  },
  stepSub: { fontSize: 14, color: "#6b7280", margin: 0, lineHeight: 1.5 },
  inputLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#6b7280",
    marginBottom: 5,
    letterSpacing: ".02em",
  },
  inlineErr: { fontSize: 12, color: "#ef4444", margin: 0 },
  errorBanner: {
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#dc2626",
    marginBottom: 4,
  },
  infoBox: {
    background: "#f9fafb",
    borderRadius: 12,
    padding: "4px 14px",
    border: "1px solid #eaecf0",
  },
  infoLabel: { fontSize: 12, color: "#9ca3af", margin: 0 },
  infoMono: {
    fontFamily: "'DM Mono',monospace",
    color: "#111",
    margin: 0,
    fontWeight: 500,
  },
  pendingRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 0",
  },
  resolvedBox: {
    background: "#f0f9f4",
    border: "1.5px solid #86efac",
    borderRadius: 12,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  resolvedIcon: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: "#22c55e",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  },
  successRing: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#eff6ff",
    border: "2px solid #bfdbfe",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  failRing: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "#fef2f2",
    border: "2px solid #fecaca",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  summaryFooter: { borderTop: "1px solid #f3f4f6", margin: "20px 0 0" },
  summaryToggle: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 24px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    transition: "background .15s",
  },
  summaryBody: {
    padding: "0 24px 16px",
    display: "flex",
    flexDirection: "column",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
  },
};

const dark: Theme = {
  ...light,
  modal: {
    ...light.modal,
    background: "#12141f",
    boxShadow: "0 32px 80px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.06)",
  },
  closeBtn: {
    ...light.closeBtn,
    border: "1.5px solid #2a2d3a",
    color: "#4a4f6a",
  },
  stepTitle: { ...light.stepTitle, color: "#f0f1f5" },
  stepSub: { ...light.stepSub, color: "#8892a4" },
  inputLabel: { ...light.inputLabel, color: "#8892a4" },
  errorBanner: {
    ...light.errorBanner,
    background: "rgba(239,68,68,.08)",
    border: "1px solid rgba(239,68,68,.25)",
    color: "#f87171",
  },
  infoBox: {
    background: "#1a1d27",
    borderRadius: 12,
    padding: "4px 14px",
    border: "1px solid #2a2d3a",
  },
  infoLabel: { fontSize: 12, color: "#4a4f6a", margin: 0 },
  infoMono: {
    fontFamily: "'DM Mono',monospace",
    color: "#f0f1f5",
    margin: 0,
    fontWeight: 500,
  },
  resolvedBox: {
    background: "rgba(34,197,94,.06)",
    border: "1.5px solid rgba(34,197,94,.25)",
    borderRadius: 12,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  resolvedIcon: {
    ...light.resolvedIcon,
    background: "rgba(34,197,94,.2)",
    color: "#22c55e",
  },
  successRing: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "rgba(61,126,255,.1)",
    border: "2px solid rgba(61,126,255,.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  failRing: {
    ...light.failRing,
    background: "rgba(239,68,68,.1)",
    border: "2px solid rgba(239,68,68,.25)",
  },
  summaryFooter: { borderTop: "1px solid #1e2130", margin: "20px 0 0" },
  logoText: { ...light.logoText, color: "#f0f1f5" },
};

// ─── KYC helpers ─────────────────────────────────────────────────────────────
function kycPlaceholder(t: string): string {
  return (
    (
      {
        FULL_NAME: "Jane Doe",
        PHONE: "+2348012345678",
        BVN: "11-digit BVN",
        NIN: "11-digit NIN",
        EMAIL: "email@domain.com",
        DOB: "MM/DD/YYYY",
        ADDRESS: "123 Main Street, Lagos",
      } as Record<string, string>
    )[t] ?? t
  );
}
